import * as IoC from 'typescript-ioc';
import { action, observable } from 'mobx';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IMeetingsTemplateField, IMeetingTemplateDTO, MeetingTemplateAction } from '../../../../../api/blueprints/meeting.template.types';
import { IBlueprintsDAO } from '../../../../../api/blueprints/blueprints.dao';
import { TObject } from '../../../../../infrastructure/type-extensions';
import { IConnectionTemplateDTO } from '../../../../../api/blueprints/connection.template.types';

type MeetingTemplateFieldsValues = {
    [key in IMeetingsTemplateField['name']]: IMeetingsTemplateField['value']
};

export class MeetingFormTemplatesVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @observable
    private templates: TObject<IMeetingTemplateDTO> = {};
    @observable
    private connectionsTemplates: IConnectionTemplateDTO[];
    @observable
    private id: string | null;
    @observable
    private loading: boolean = false;

    constructor(
        templateId: string | null = null,
        private readonly accountId: number,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
    ) {
        this.id = templateId;
    }

    @action
    public async initialize(): Promise<void> {
        this.startLoading();

        try {
            const params = { accountId: this.accountId };
            const meetings = await this.blueprintsDAO.getMeetingTemplateList(params);
            this.setMeetingTemplates(meetings);

            const connections = await this.blueprintsDAO.getConnectionTemplateList(params);
            this.setConnectionsTemplates(connections);
        } catch (error) {
            MeetingFormTemplatesVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    @action
    public setTemplateId(id: string): void {
        this.id = id || null;
    }

    public getTemplateId(): string | null {
        return this.id;
    }

    public getTemplateName(): string {
        return this.templates[this.id]?.name ?? '';
    }

    public getSelectTemplate(): IMeetingTemplateDTO | null {
        return this.templates[this.id] ?? null;
    }

    public getTemplateList(): IMeetingTemplateDTO[] {
        return Object.values(this.templates);
    }

    public getConnectionTemplatesList(): IConnectionTemplateDTO[] {
        return this.connectionsTemplates;
    }

    public getTemplateFieldsValues(): MeetingTemplateFieldsValues {
        return this.getTemplateFields()
            .reduce((fieldsValues, { name, value }) => {
                fieldsValues[name] = value;
                return fieldsValues;
            }, {} as MeetingTemplateFieldsValues)
            ;
    }

    public templateHasActions(): boolean {
        return !!this.getTemplateActions().length;
    }

    private getTemplateFields(): IMeetingsTemplateField[] {
        return this.templates[this.id]?.fields ?? [];
    }

    private getTemplateActions(): MeetingTemplateAction[] {
        return this.templates[this.id]?.actions ?? [];
    }

    @action
    private setMeetingTemplates(meetingTemplates: IMeetingTemplateDTO[]): void {
        const templates = {};

        meetingTemplates
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(template => {
                templates[template.blueprintId] = template;
            })
            ;

        this.templates = templates;
    }

    @action
    private setConnectionsTemplates(templates: IConnectionTemplateDTO[]): void {
        this.connectionsTemplates = templates;
    }

    @action
    private startLoading(): void {
        this.loading = true;
    }
    @action
    private stopLoading(): void {
        this.loading = false;
    }
}
